/**
 * 日焼けあとのケア記録。
 *
 * 「浴びた量」を記録するだけでは行動が変わらないため、浴びたあとに何をして
 * どうだったかを残し、自分に合うやり方を見つけられるようにする。
 *
 * ⚠️ 薬機法・SaMD回避方針（設計書§1.3「非対象：疾病の予防・診断の標榜」）：
 *   ここで扱うのは一般的な食品・スキンケアの分類と、本人の主観記録だけである。
 *   効果・効能をうたわず、治療や予防を目的としない。文言も「〜が治る」ではなく
 *   「〜を摂った」という事実の記録に留める。写真は端末内のみに保存する。
 */

/** 日焼けの自己申告レベル（本人の主観。医学的診断ではない） */
export const SUNBURN_LEVELS = [
  { value: 0, label: "変化なし" },
  { value: 1, label: "少し赤い" },
  { value: 2, label: "赤い・ヒリヒリ" },
  { value: 3, label: "強い赤み・痛み" },
] as const;
export type SunburnLevel = (typeof SUNBURN_LEVELS)[number]["value"];

/** 記録用の食品カテゴリ。栄養素の一般的な分類であり、効能をうたうものではない */
export const FOOD_TAGS = [
  { id: "vitc", label: "ビタミンCの多い食品", examples: "パプリカ・ブロッコリー・キウイ" },
  { id: "vite", label: "ビタミンEの多い食品", examples: "ナッツ・アボカド" },
  { id: "carotene", label: "β-カロテンの多い食品", examples: "にんじん・かぼちゃ" },
  { id: "lycopene", label: "リコピンの多い食品", examples: "トマト" },
  { id: "protein", label: "たんぱく質", examples: "肉・魚・卵・大豆" },
  { id: "water", label: "水分をしっかり摂った", examples: "水・お茶" },
] as const;

/** 記録用のスキンケア行動 */
export const CARE_TAGS = [
  { id: "cool", label: "冷やした" },
  { id: "mask", label: "フェイスパック" },
  { id: "moist", label: "保湿クリーム・化粧水" },
  { id: "sleep", label: "早めに寝た" },
  { id: "exercise", label: "運動した" },
  { id: "nothing", label: "特に何もしていない" },
] as const;

/**
 * 運動・睡眠の記録。
 * ⚠️ PWAからApple Watch（HealthKit）を直接読むことはできない。iOSのWebには
 * HealthKit APIが無いため、次のいずれかになる：
 *   - 手入力（いまの実装）
 *   - ヘルスケアアプリの書き出し（XML）を取り込む（`source: "import"`）
 *   - Capacitorのネイティブ殻に移行して直接読む（§7.2・§13と同じ移行話）
 */
export interface ActivityRecord {
  /** 睡眠時間 [時間] */
  sleepHours: number | null;
  /** 歩数 */
  steps: number | null;
  /** 運動時間 [分] */
  exerciseMin: number | null;
  source: "manual" | "import";
}

export type FoodTagId = (typeof FOOD_TAGS)[number]["id"];
export type CareTagId = (typeof CARE_TAGS)[number]["id"];

/** VLM経由で来る文字列を検証する（サーバの返り値を無検証で採用しない、§8.4と同じ方針） */
export function isFoodTagId(v: unknown): v is FoodTagId {
  return typeof v === "string" && FOOD_TAGS.some((f) => f.id === v);
}

/** 1件の記録。写真は端末内のみ（サーバへ送らない、§2・§11.1） */
export interface CareEntry {
  /** 記録時刻（epoch ms）。主キー */
  ts: number;
  /**
   * どのユーザーの記録か（端末内アカウントのid、accounts.ts）。
   * アカウントを入れる前に作られた記録には無い。移行のとき最初のユーザーが引き取る。
   */
  userId?: string;
  /** ケア前の顔写真。同条件で比較できるよう画面フラッシュ下で撮る */
  photo: Blob | null;
  /** ケア後の顔写真（同じ日の後刻に撮る） */
  photoAfter: Blob | null;
  /** 撮影時に画面フラッシュを使ったか（使っていない写真とは明るさが揃わない） */
  flashUsed: boolean;
  level: SunburnLevel;
  foods: FoodTagId[];
  cares: CareTagId[];
  /** 食事の写真（何を食べたかの記録。解析はせず記録として残す） */
  mealPhotos: Blob[];
  /** レシートから読み取った品目（VLM抽出＋世帯人数で案分した概算） */
  receiptItems: ReceiptItem[];
  activity: ActivityRecord | null;
  note: string;
  /** その日に浴びた量 [SED]（記録時点の日次集計から控える） */
  sedThatDay: number;
  /** その日のPM2.5曝露 [μg/m³·日] */
  pm25ThatDay: number;
}

/** レシートから読み取った1品。世帯で分け合う前提の概算である点に注意 */
export interface ReceiptItem {
  name: string;
  /** 該当する食品カテゴリ（分からなければ null） */
  category: FoodTagId | null;
  /** 世帯人数で案分した1人あたりの個数・回数の概算 */
  perPerson: number;
}

/**
 * レシートの品目を世帯人数で案分する。
 * 家族で分け合うため、買った量がそのまま本人の摂取ではない。あくまで概算であり、
 * 誰がどれだけ食べたかは分からない点を画面でも断る。
 */
export function prorateReceipt(items: { name: string; quantity: number; category: FoodTagId | null }[], householdSize: number): ReceiptItem[] {
  const n = Math.max(1, Math.floor(householdSize));
  return items.map((i) => ({
    name: i.name,
    category: i.category,
    perPerson: i.quantity / n,
  }));
}

/** 一覧表示のための説明文（効能をうたわない表現に統一） */
export const CARE_DISCLAIMER =
  "このアプリは医療機器ではありません。ここに出る食品・ケアの分類は一般的な情報で、" +
  "日焼けの治療や予防を目的とするものではありません。記録は自分の傾向を見るためのものです。";

export const FLASH_NOTE =
  "撮影の直前に画面が白く光ります。毎回同じ明るさで撮ると、後から比べやすくなります。";

export const RECEIPT_NOTE =
  "レシートの品目は世帯人数で割った概算です。誰がどれだけ食べたかまでは分かりません。";

export const ACTIVITY_NOTE =
  "Apple Watchなどからの自動取り込みは、いまのWeb版では行えません（iOSのWebから" +
  "ヘルスケアを読む手段がないため）。手入力か、ヘルスケアの書き出しファイルの取り込みでお願いします。";

/**
 * 記録の傾向を集計する。「このケアをした日は自己申告が軽い傾向」という
 * 相関の提示までに留め、因果や効果は主張しない（件数も併記する）。
 */
export interface CareTrend {
  tag: string;
  label: string;
  /** そのタグを付けた記録の平均レベル */
  avgLevel: number;
  count: number;
}

export function summarizeCare(entries: CareEntry[]): {
  foods: CareTrend[];
  cares: CareTrend[];
  overallAvg: number;
  total: number;
} {
  const avgOf = (list: CareEntry[]): number =>
    list.length > 0 ? list.reduce((s, e) => s + e.level, 0) / list.length : 0;

  const foods = FOOD_TAGS.map((t) => {
    const hit = entries.filter((e) => e.foods.includes(t.id));
    return { tag: t.id, label: t.label, avgLevel: avgOf(hit), count: hit.length };
  }).filter((t) => t.count > 0);

  const cares = CARE_TAGS.map((t) => {
    const hit = entries.filter((e) => e.cares.includes(t.id));
    return { tag: t.id, label: t.label, avgLevel: avgOf(hit), count: hit.length };
  }).filter((t) => t.count > 0);

  const byLevel = (a: CareTrend, b: CareTrend) => a.avgLevel - b.avgLevel || b.count - a.count;
  return {
    foods: foods.sort(byLevel),
    cares: cares.sort(byLevel),
    overallAvg: avgOf(entries),
    total: entries.length,
  };
}
